import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest, HttpResponse, HttpStatusCode } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ErrorModalComponent } from '../../dialogs/error-modal';
import { ErrorInfo } from '../../models/error-info.model';
import { AppConfigService } from '../../services/app-config-service/app-config.service';
import { SpinnerOverlayService } from '../../services/spinner-overlay/spinner-overlay.service';
import { StateService } from '../../services/state-service/state.service';

const CONFIGURATION_ID_ERROR_KEY = 'configurationIdError';

export function httpInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const router = inject(Router);
  const stateService = inject(StateService);
  const spinnerService = inject(SpinnerOverlayService);
  const appConfigService = inject(AppConfigService);
  const modalService = inject(NgbModal);

  spinnerService.show();

  return next(req).pipe(
    tap({
      next: (event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          spinnerService.hide();
        }
      },
      error: () => {
        spinnerService.hide();
      },
    }),
    catchError((error: HttpErrorResponse) => {
      switch (error.status) {
        case HttpStatusCode.Unauthorized: {
          router.navigateByUrl('/authenticate').then();
          break;
        }
        case HttpStatusCode.Forbidden: {
          router.navigateByUrl(stateService.getRedirectPathByAssessmentState((<ErrorInfo>error.error).state)).then();
          break;
        }
        case HttpStatusCode.NotFound: {
          if (error?.error) {
            let errorInfo: ErrorInfo;
            if (typeof error.error === 'string') {
              errorInfo = JSON.parse(error.error) as ErrorInfo;
            } else {
              errorInfo = <ErrorInfo>error.error;
            }
            if (errorInfo.translationKey === 'rspErrorAssessmentNotFound') {
              return throwError(() => new Error(errorInfo.translationKey));
            }
          }
          showErrorModal(errorModalTranslationKey(error), modalService).result.then(() => {
            router.navigateByUrl('/welcome').then();
          });
          break;
        }
        case HttpStatusCode.InternalServerError: {
          router.navigateByUrl('/error').then();
          break;
        }
        case HttpStatusCode.BadRequest: {
          if (error.error?.key === CONFIGURATION_ID_ERROR_KEY) {
            console.error(error.error.message);
            // prohibit incorrect configurationIds to be stored so that the user can go back to the welcome page using the default ID
            appConfigService.deleteStoredConfigurationId(error.error.configurationId);
            break;
          }
          showErrorModal(errorModalTranslationKey(error), modalService);
          break;
        }
        default: {
          showErrorModal(errorModalTranslationKey(error), modalService);
          break;
        }
      }
      return throwError(() => new Error(error.message));
    })
  );
}

function errorModalTranslationKey(error: HttpErrorResponse): string {
  if (error.error instanceof ErrorInfo) {
    return error.error.translationKey;
  } else {
    return 'rspModalGeneralBody';
  }
}

function showErrorModal(modalBodyKey: string, modalService: NgbModal): NgbModalRef {
  let modal: NgbModalRef = modalService.open(ErrorModalComponent, {
    ariaLabelledBy: 'error-modal-title',
    ariaDescribedBy: 'error-modal-body',
  });
  modal.componentInstance.modalBodyKey = modalBodyKey;
  modal.componentInstance.modalTitleKey = 'rspModalGeneralHeading';
  modal.componentInstance.modalButtonCloseKey = 'rspModalButtonClose';
  return modal;
}
